import httpClient from '../httpClient';

// ===== INTERFACES =====

export interface DashboardStat {
  title: string;
  value: number;
  increase: string;
  iconType: 'book' | 'alert' | 'check' | 'users';
}

export interface CourseProgress {
  name: string;
  enrolled: number;
  total: number;
}

export interface Task {
  id: string;
  type: 'Review' | 'Grading' | 'Feedback' | 'Approval';
  status: 'Pending' | 'Urgent';
  description: string;
  dueDate: string;
  action: string;
}

export interface LearningHoursData {
  week: string;
  hours: number;
}

export interface LearningProgress {
  totalHours: number;
  progressPercentage: number;
  trend: 'up' | 'down' | 'neutral';
  trendValue: number;
  currentMonth: string;
}

export interface StudentPerformance {
  studentId: string;
  studentName: string;
  studentEmail: string;
  courseName: string;
  totalHours: number;
  avgScore: number;
  enrolledDate: string;
}

export interface DateRange {
  startDate: Date;
  endDate: Date;
}

export type TaskActionType = 'approve' | 'reject' | 'remind' | 'complete';

// ===== HELPER FUNCTIONS =====

const buildQueryParams = (dateRange?: DateRange, additionalParams?: Record<string, string | number>): string => {
  const params = new URLSearchParams();

  if (dateRange) {
    params.append('startDate', dateRange.startDate.toISOString().split('T')[0]);
    params.append('endDate', dateRange.endDate.toISOString().split('T')[0]);
  }

  if (additionalParams) {
    Object.entries(additionalParams).forEach(([key, value]) => {
      params.append(key, String(value));
    });
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

// ===== API Response Interface =====
interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

// ===== SERVICE FUNCTIONS =====

export const getDashboardStats = async (dateRange?: DateRange): Promise<{
  success: boolean;
  data?: DashboardStat[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange);
    const response = await httpClient.get<ApiResponse<DashboardStat[]>>(
      `/instructor/dashboard/stats${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('InstructorDashboardService: Error fetching stats:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch dashboard stats' }
    };
  }
};

export const getTopCourses = async (dateRange?: DateRange, limit: number = 5): Promise<{
  success: boolean;
  data?: CourseProgress[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange, { limit });
    const response = await httpClient.get<ApiResponse<CourseProgress[]>>(
      `/instructor/dashboard/top-courses${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('InstructorDashboardService: Error fetching courses:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch courses' }
    };
  }
};

export const getTasks = async (dateRange?: DateRange, limit: number = 10): Promise<{
  success: boolean;
  data?: Task[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange, { limit });
    const response = await httpClient.get<ApiResponse<Task[]>>(
      `/instructor/dashboard/tasks${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('InstructorDashboardService: Error fetching tasks:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch tasks' }
    };
  }
};

export const getLearningHoursData = async (dateRange?: DateRange): Promise<{
  success: boolean;
  data?: LearningHoursData[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange);
    const response = await httpClient.get<ApiResponse<LearningHoursData[]>>(
      `/instructor/dashboard/learning-hours${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('InstructorDashboardService: Error fetching learning hours:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learning hours data' }
    };
  }
};

export const getLearningProgress = async (dateRange?: DateRange): Promise<{
  success: boolean;
  data?: LearningProgress;
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange);
    const response = await httpClient.get<ApiResponse<LearningProgress>>(
      `/instructor/dashboard/learning-progress${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('InstructorDashboardService: Error fetching learning progress:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch learning progress' }
    };
  }
};

export const getStudentPerformance = async (dateRange?: DateRange): Promise<{
  success: boolean;
  data?: StudentPerformance[];
  error?: { message: string } | null;
}> => {
  try {
    const queryParams = buildQueryParams(dateRange);
    const response = await httpClient.get<ApiResponse<StudentPerformance[]>>(
      `/instructor/dashboard/student-performance${queryParams}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('InstructorDashboardService: Error fetching student performance:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch student performance' }
    };
  }
};

export const performTaskAction = async (
  taskId: string,
  action: TaskActionType
): Promise<{
  success: boolean;
  data?: { message: string };
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.post<ApiResponse<{ message: string }>>(
      `/instructor/dashboard/tasks/${taskId}/action`,
      { action }
    );

    return {
      success: response.success,
      data: { message: response.message || 'Action completed successfully' },
      error: null
    };
  } catch (error: any) {
    console.error(`InstructorDashboardService: Error performing ${action} on task:`, error);
    return {
      success: false,
      error: { message: error?.message ?? `Failed to ${action} task` }
    };
  }
};

export const getTaskById = async (taskId: string): Promise<{
  success: boolean;
  data?: Task;
  error?: { message: string } | null;
}> => {
  try {
    const response = await httpClient.get<ApiResponse<Task>>(
      `/instructor/dashboard/tasks/${taskId}`
    );

    return {
      success: true,
      data: response.data,
      error: null
    };
  } catch (error: any) {
    console.error('InstructorDashboardService: Error fetching task:', error);
    return {
      success: false,
      error: { message: error?.message ?? 'Failed to fetch task' }
    };
  }
};
